<template>
  <b-container>
    <b-card>
      <canvas ref="probability-chart" width="160" height="90" />
    </b-card>
  </b-container>
</template>

<i18n>
{
  "en": {
    "per_observation_probability": "Per observation probability",
    "probability": "Probability"
  },
  "fr": {
    "per_observation_probability": "Probabilité par observation",
    "probability": "Probabilité"
  }
}
</i18n>

<script>
import _ from 'lodash'
import Chart from 'chart.js'

import { get_columnstore_datav2 } from '@/api/subscriptions.js'
import chartColors from '@/components/chart_colors.js'

import { subscription_mixin } from '@/components/mixin.js'

// *****************************************************************************

class TrueProbabilityChart {
  constructor(el, title) {
    this.el = el
    this.title = title
  }
  init() {
    this.real_chart = new Chart(this.el, {
      type: 'line',
      data: {
        labels: [],
        datasets: [],
      },
      options: {
        responsive: true,
        hover: {
          mode: 'nearest',
          intersect: false,
        },
        plugins: {
          datalabels: {
            display: false,
          },
        },
        title: {
          display: true,
          text: this.title,
        },
        elements: {
          point: {
            radius: 0,
          },
        },
      },
    })
  }

  update(datasets, nb_bins, nb_obs_per_bin) {
    this.real_chart.data.labels = _.map(
      _.range(nb_bins),
      x => x * nb_obs_per_bin
    )
    this.real_chart.data.datasets = datasets
    this.real_chart.update()
  }
}

// *****************************************************************************

export default {
  name: 'TrueProbabilityChart',
  components: {},
  mixins: [subscription_mixin],
  props: ['project_id', 'poid'],
  data() {
    return {
      chart_object: undefined,
      is_mounted: false,
      datasets_options_base: {
        cubicInterpolationMode: 'monotone',
        fill: false,
      },
      chart_datasets: [],
      datasets: [],
      nb_bins: 0,
      nb_obs_per_bin: 0,
    }
  },
  computed: {
    title() {
      return this.$t('per_observation_probability')
    },
    poid_instantiated() {
      return this.fetch_poid_status('poid_instantiated', this.poid)
    },
  },
  watch: {
    poid_instantiated: {
      immediate: true,
      handler(new_val, old_val) {
        this.build_dataset()
      },
    },
  },
  beforeCreate() {},
  mounted() {
    this.is_mounted = true
    this.check_chart()
  },
  beforeDestroy() {
    if (this.chart_object) {
      this.chart_object.real_chart.destroy()
    }
  },
  methods: {
    check_chart(reinit = false) {
      if (!this.is_mounted) {
        return
      }
      if (this.chart_object === undefined || reinit) {
        if (this.chart_object) {
          this.chart_object.real_chart.destroy()
        }
        this.chart_object = new TrueProbabilityChart(
          this.$refs['probability-chart'],
          this.title
        )
        this.chart_object.init()
        this.chart_datasets = []
      }
      for (let dataset of this.datasets) {
        let existing_dataset = _.find(this.chart_datasets, {
          label: dataset.label,
        })
        if (_.isUndefined(existing_dataset)) {
          this.chart_datasets.push(dataset)
        } else {
          existing_dataset.data = dataset.data
        }
      }
      this.chart_object.update(
        this.chart_datasets,
        this.nb_bins,
        this.nb_obs_per_bin
      )
    },

    async build_dataset() {
      let data = []
      let nb_bins = 0
      let nb_obs_per_bin = 1
      if (this.poid_instantiated) {
        let width = 1024
        let payload = await get_columnstore_datav2(
          this.project_id,
          this.poid,
          ['decision'],
          null,
          { bins: width, sorted: true, reversed: true }
        )
        data = payload.pipe_object[0]
        nb_bins = data.length
        nb_obs_per_bin = payload.nb_obs_per_bin
      }
      let datasets = [
        _.merge(
          {
            label: this.$t('probability'),
            backgroundColor: chartColors.blue,
            borderColor: chartColors.blue,
            data,
          },
          this.datasets_options_base
        ),
      ]
      this.datasets = datasets
      this.nb_bins = nb_bins
      this.nb_obs_per_bin = nb_obs_per_bin
      this.check_chart()
    },
  },
}
</script>
